import React, { useEffect, useMemo, useState } from 'react';

const CONTACT_STORAGE_KEY = 'dodsego:stockRequestContact';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeInput(value, maxLen) {
  return String(value || '').trim().slice(0, maxLen);
}

function normalizeSelectedVariants(input) {
  const source = Array.isArray(input) ? input : [];
  return source
    .map((entry) => ({
      title: normalizeInput(entry?.title, 120),
      value: normalizeInput(entry?.value, 120),
    }))
    .filter((entry) => entry.title && entry.value);
}

function formatVariantSummary(selectedVariants) {
  const rows = normalizeSelectedVariants(selectedVariants);
  if (!rows.length) return 'Standard';
  return rows.map((entry) => `${entry.title}: ${entry.value}`).join(', ');
}

const EMPTY_FORM = {
  customerName: '',
  customerEmail: '',
  requestText: '',
};

const MerchandiseStockRequestModal = ({
  open = false,
  pending = false,
  productTitle = '',
  selectedVariants = [],
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) return;
    try {
      const raw = window.localStorage.getItem(CONTACT_STORAGE_KEY);
      if (!raw) {
        setForm(EMPTY_FORM);
        setSubmitError('');
        return;
      }
      const parsed = JSON.parse(raw);
      setForm({
        customerName: normalizeInput(parsed?.customerName, 120),
        customerEmail: normalizeInput(parsed?.customerEmail, 320).toLowerCase(),
        requestText: '',
      });
      setSubmitError('');
    } catch {
      setForm(EMPTY_FORM);
      setSubmitError('');
    }
  }, [open]);

  const variantSummary = useMemo(() => formatVariantSummary(selectedVariants), [selectedVariants]);
  const title = normalizeInput(productTitle, 240) || 'Merchandise';

  if (!open) return null;

  const closeModal = () => {
    if (pending) return;
    setSubmitError('');
    if (typeof onClose === 'function') onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pending) return;

    const customerName = normalizeInput(form.customerName, 120);
    const customerEmail = normalizeInput(form.customerEmail, 320).toLowerCase();
    const requestText = normalizeInput(form.requestText, 2000);

    if (customerName.length < 2) {
      setSubmitError('Please enter your name.');
      return;
    }
    if (!EMAIL_RE.test(customerEmail)) {
      setSubmitError('Please enter a valid email.');
      return;
    }
    if (requestText.length < 2) {
      setSubmitError('Please enter your request.');
      return;
    }

    try {
      const result = await Promise.resolve(onSubmit?.({ customerName, customerEmail, requestText }));
      if (result === false || (result && result.ok === false)) {
        setSubmitError(String(result?.message || 'Could not send request.'));
        return;
      }
      try {
        window.localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify({ customerName, customerEmail }));
      } catch {}
      setSubmitError('');
    } catch {
      setSubmitError('Could not send request.');
    }
  };

  return (
    <div
      onClick={closeModal}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1260,
        background: 'rgba(0, 0, 0, 0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
      }}
    >
      <form
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        style={{
          width: 'min(540px, 96vw)',
          borderRadius: 14,
          border: '1px solid rgba(255, 255, 255, 0.28)',
          background: 'linear-gradient(150deg, rgba(28, 33, 36, 0.97), rgba(8, 8, 10, 0.97))',
          color: '#fff',
          padding: '18px 18px 16px',
          display: 'grid',
          gap: 12,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={{ textTransform: 'uppercase', letterSpacing: '0.11em', fontSize: 12, opacity: 0.82 }}>
            OUT OF STOCK REQUEST
          </div>
          <h3 style={{ margin: 0, fontSize: 24, lineHeight: 1.1 }}>{title}</h3>
          <p style={{ margin: '4px 0 0', opacity: 0.94, lineHeight: 1.35 }}>
            Please send us your request and we will come back to you
          </p>
          <p style={{ margin: 0, opacity: 0.75, fontSize: 13 }}>
            Variant: {variantSummary}
          </p>
        </div>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, opacity: 0.88 }}>Your Name</span>
          <input
            type='text'
            maxLength={120}
            value={form.customerName}
            onChange={(event) => setForm((prev) => ({ ...prev, customerName: event.target.value }))}
            disabled={pending}
            style={{
              width: '100%',
              borderRadius: 9,
              border: '1px solid rgba(255, 255, 255, 0.28)',
              background: 'rgba(0, 0, 0, 0.42)',
              color: '#fff',
              padding: '10px 12px',
              boxSizing: 'border-box',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, opacity: 0.88 }}>Your Email</span>
          <input
            type='email'
            maxLength={320}
            value={form.customerEmail}
            onChange={(event) => setForm((prev) => ({ ...prev, customerEmail: event.target.value }))}
            disabled={pending}
            style={{
              width: '100%',
              borderRadius: 9,
              border: '1px solid rgba(255, 255, 255, 0.28)',
              background: 'rgba(0, 0, 0, 0.42)',
              color: '#fff',
              padding: '10px 12px',
              boxSizing: 'border-box',
            }}
          />
        </label>

        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 13, opacity: 0.88 }}>You request</span>
          <textarea
            rows={5}
            maxLength={2000}
            value={form.requestText}
            onChange={(event) => setForm((prev) => ({ ...prev, requestText: event.target.value }))}
            disabled={pending}
            style={{
              width: '100%',
              borderRadius: 9,
              border: '1px solid rgba(255, 255, 255, 0.28)',
              background: 'rgba(0, 0, 0, 0.42)',
              color: '#fff',
              padding: '10px 12px',
              boxSizing: 'border-box',
              resize: 'vertical',
              minHeight: 110,
            }}
          />
        </label>

        {submitError ? (
          <div style={{ borderRadius: 8, border: '1px solid rgba(255, 130, 130, 0.55)', background: 'rgba(92, 8, 8, 0.35)', padding: '8px 10px', fontSize: 13 }}>
            {submitError}
          </div>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type='button'
            onClick={closeModal}
            disabled={pending}
            style={{
              borderRadius: 8,
              border: '1px solid #4a4a4a',
              background: '#151515',
              color: '#fff',
              padding: '9px 12px',
              cursor: pending ? 'default' : 'pointer',
            }}
          >
            CANCEL
          </button>
          <button
            type='submit'
            disabled={pending}
            style={{
              borderRadius: 8,
              border: '1px solid rgba(133, 255, 185, 0.7)',
              background: 'rgba(10, 74, 39, 0.9)',
              color: '#e6fff0',
              padding: '9px 14px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              cursor: pending ? 'default' : 'pointer',
              opacity: pending ? 0.74 : 1,
            }}
          >
            {pending ? 'SENDING REQUEST...' : 'SEND REQUEST'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MerchandiseStockRequestModal;
